import styled from 'styled-components';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { Risk } from './types';

const CHIP_COLORS: Record<Risk, { background: string; color: string }> = {
  view: {
    background: FullPalette.EVERGREEN_E100,
    color: FullPalette.EVERGREEN_700,
  },
  warning: {
    background: FullPalette.ZINNIA_E200,
    color: FullPalette.EVERGREEN_700,
  },
  error: {
    background: FullPalette.POPPY_E300,
    color: FullPalette.EVERGREEN_700,
  },
};

export const FamilyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Tokens.spacing[1]};
  cursor: pointer;
  user-select: none;
`;

export const FamilyTitleBlock = styled.div`
  flex: 1;
`;

export const ArrowIconWrapper = styled.span<{ $isOpen: boolean }>`
  button {
    ${(props) => !props.$isOpen && 'transform: rotate(-90deg)'};
    transition: transform 0.2s ease;
  }
`;

export const TierPillRow = styled.span`
  display: flex;
  gap: ${Tokens.spacing[0.5]};
  flex-shrink: 0;
`;

export const TierCountPill = styled.span<{ $risk: Risk }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: ${({ $risk }) => CHIP_COLORS[$risk].background};
  color: ${({ $risk }) => CHIP_COLORS[$risk].color};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Tokens.spacing[1]};
`;

export const ChipButton = styled.button<{ $risk: Risk; $selected: boolean }>`
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    border-color 0.15s ease;
  border: 1px solid
    ${({ $risk, $selected }) =>
      $selected ? CHIP_COLORS[$risk].background : FullPalette.GREY_600};
  background-color: ${({ $risk, $selected }) =>
    $selected ? CHIP_COLORS[$risk].background : FullPalette.WHITE};
  color: ${({ $risk, $selected }) =>
    $selected ? CHIP_COLORS[$risk].color : FullPalette.EVERGREEN_600};

  &:focus-visible {
    outline: 2px solid ${Tokens.colors.selected};
    outline-offset: 1px;
  }
`;

export const ChipBadge = styled.span<{ $risk: Risk }>`
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${({ $risk }) => CHIP_COLORS[$risk].background};
  background-color: ${({ $risk }) => CHIP_COLORS[$risk].background};
  color: ${({ $risk }) => CHIP_COLORS[$risk].color};
`;

const DOT_COLORS: Record<Risk, string> = {
  view: FullPalette.EVERGREEN_E400,
  warning: FullPalette.ZINNIA_500,
  error: FullPalette.POPPY_600,
};

export const BulkActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Tokens.spacing[1]};
`;

export const TierDot = styled.span<{ $risk: Risk }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $risk }) => DOT_COLORS[$risk]};
  flex-shrink: 0;
`;

export const TierLabel = styled.span`
  min-width: 48px;
  font-weight: 500;
`;

export const QuickSelectButton = styled.button`
  padding: 2px 12px;
  border-radius: 6px;
  border: 1px solid ${FullPalette.GREEN_700};
  background-color: ${FullPalette.WHITE};
  color: ${FullPalette.GREEN_700};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    border-color: ${FullPalette.EVERGREEN_700};
    color: ${FullPalette.EVERGREEN_700};
  }

  &:focus-visible {
    outline: 2px solid ${Tokens.colors.selected};
    outline-offset: 1px;
  }
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const ResourceRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing[0.5]};
  padding-left: 24px;
`;
